import { ApiClient, ApiError } from "./api-client.js";
import type { View } from "./view.js";

export type ControllerDependencies = {
  readonly view: View;
  readonly api: Pick<ApiClient, "submitPrompt">;
};

/**
 * Client-side behaviour, with no DOM and no network in sight — both arrive as
 * injected collaborators, so every path here is testable with plain objects.
 */
export class PromptController {
  private inFlight = false;

  constructor(private readonly deps: ControllerDependencies) {}

  /** Attaches to the view. Call once. */
  start(): void {
    this.deps.view.render({ kind: "idle" });
    this.deps.view.onSubmit(() => {
      void this.submit();
    });
  }

  async submit(): Promise<void> {
    // Guards against double submits from repeated clicks or Enter presses.
    if (this.inFlight) return;

    const text = this.deps.view.getPromptText().trim();

    if (text.length === 0) {
      this.deps.view.render({
        kind: "error",
        message: "Please enter a prompt first.",
      });
      return;
    }

    this.inFlight = true;
    this.deps.view.render({ kind: "submitting" });

    try {
      const submission = await this.deps.api.submitPrompt(text);
      this.deps.view.render({ kind: "result", prompt: submission.response });
    } catch (error) {
      this.deps.view.render({
        kind: "error",
        message: describeFailure(error),
      });
    } finally {
      this.inFlight = false;
    }
  }
}

function describeFailure(error: unknown): string {
  if (error instanceof ApiError) return error.message;

  return "Could not reach the server. Please check your connection.";
}
